import { useState } from "react";
import { MessageCircle, Send, Copy, Contact } from "lucide-react";
import { rgApi, fmtLongDate } from "@/lib/rgApi";
import { MESSAGES as MSG_DEFAULTS } from "./rgMessages";

/**
 * CommButtons — actions de communication rapides (bloc 1, en haut de la fiche voyageur).
 *  - Appel direct
 *  - WhatsApp libre (message vide, pré-remplit le numéro)
 *  - SMS libre
 *  - Email (mailto)
 *  - Dropdown "Envoyer un modèle" (Confirmation / Jour J / Merci) qui pré-remplit WA ou SMS
 *    et log l'envoi dans la timeline.
 *
 * L'ÉDITION des templates se fait dans Réglages → Templates.
 */
export default function CommButtons({ reservation, templates, onLogSend, showContact = true, showTemplate = true }) {
  const [tplPickerOpen, setTplPickerOpen] = useState(false);
  const cleanPhone = (reservation?.phone || "").replace(/[^\d+]/g, "");
  const waPhone = cleanPhone.replace(/^\+?/, "");

  const render = (body) =>
    (body || "")
      .replaceAll("{name}", reservation?.name || "")
      .replaceAll("{first_name}", (reservation?.name || "").split(" ")[0])
      .replaceAll("{date_arrival}", fmtLongDate(reservation?.date_arrival))
      .replaceAll("{date_departure}", fmtLongDate(reservation?.date_departure));

  const TPL_KEYS = [
    { key: "confirmation", label: "Confirmation", dbKey: "confirmation", fallback: MSG_DEFAULTS.confirmation.body },
    { key: "arrivee",      label: "Jour J",       dbKey: "jour_arrivee", fallback: MSG_DEFAULTS.arrivee.body },
    { key: "merci",        label: "Merci · avis", dbKey: "merci",        fallback: MSG_DEFAULTS.merci.body },
  ];

  const buildWa = (body) => {
    const encoded = encodeURIComponent(body);
    return waPhone ? `https://wa.me/${waPhone}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
  };
  const buildSms = (body) => {
    return cleanPhone ? `sms:${cleanPhone}?body=${encodeURIComponent(body)}` : `sms:?body=${encodeURIComponent(body)}`;
  };

  const send = (channel, tplKey) => {
    const tpl = TPL_KEYS.find((t) => t.key === tplKey);
    if (!tpl) return;
    const body = render(templates?.[tpl.dbKey] || tpl.fallback);
    const url = channel === "sms" ? buildSms(body) : buildWa(body);
    onLogSend(channel, body, url);
    setTplPickerOpen(false);
  };

  return (
    <div className="rg-row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }} data-testid="rg-comm-buttons">
      {showContact && (
        <>
          {/* vCard — Ajouter au contact (iOS/Android proposera l'ajout) */}
          <a
            href={rgApi.vcardUrl(reservation.id)}
            className="rg-btn ghost small"
            data-testid="rg-comm-vcard"
            title="Télécharger la vCard et l'ajouter à tes contacts iPhone / Android"
          >
            <Contact size={13} /> Ajouter au contact
          </a>

          {waPhone && (
            <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer" className="rg-btn ghost small" data-testid="rg-comm-wa">
              <MessageCircle size={13} /> WhatsApp
            </a>
          )}
          {cleanPhone && (
            <a href={`sms:${cleanPhone}`} className="rg-btn ghost small" data-testid="rg-comm-sms">
              <Send size={13} /> SMS
            </a>
          )}
        </>
      )}
      {/* Sélecteur de modèle → envoi WA/SMS + log timeline */}
      {showTemplate && (
      <div style={{ position: "relative" }}>
        <button
          type="button"
          className="rg-btn small"
          onClick={() => setTplPickerOpen((v) => !v)}
          disabled={!waPhone && !cleanPhone}
          data-testid="rg-tpl-picker-open"
        >
          <Copy size={12} /> Envoyer un modèle
        </button>
        {tplPickerOpen && (
          <div
            className="rg-card"
            style={{
              position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 20,
              minWidth: 260, padding: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
            }}
            data-testid="rg-tpl-picker"
            onClick={(e) => e.stopPropagation()}
          >
            {TPL_KEYS.map((t) => (
              <div key={t.key} className="rg-card" style={{ marginBottom: 6, padding: 8, background: "var(--rg-bg)" }}>
                <p className="rg-tiny" style={{ margin: "0 0 6px", fontWeight: 500 }}>{t.label}</p>
                <div className="rg-row" style={{ gap: 6 }}>
                  {waPhone && (
                    <button
                      className="rg-btn small"
                      onClick={() => send("whatsapp", t.key)}
                      data-testid={`rg-tpl-send-wa-${t.key}`}
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </button>
                  )}
                  {cleanPhone && (
                    <button
                      className="rg-btn ghost small"
                      onClick={() => send("sms", t.key)}
                      data-testid={`rg-tpl-send-sms-${t.key}`}
                    >
                      <Send size={12} /> SMS
                    </button>
                  )}
                </div>
              </div>
            ))}
            <p className="rg-tiny" style={{ margin: "6px 4px 0", color: "var(--rg-text-3)" }}>
              Édition des modèles : Réglages → Templates
            </p>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
